import {
  Card,
  Chip,
  makeStyles,
  useMediaQuery,
  useTheme,
  ActionButton,
  BulkActionBar,
  BulkActionButton,
  Button,
  Checkbox,
  ConfirmationDialog,
  Link,
  TableCell,
  TableRow,
  Typography
} from '@paintscout/ui'
import type { MuiTheme as Theme, TableCellProps, StyleClasses, ClassNameMap } from '@paintscout/ui'
import {
  ResultsTable,
  ResultsTableColumnSelect,
  useClientOptions,
  useDialogs,
  useUser,
  useUserPermissions
} from '@paintscout/ui-business'
import type { Auth0UserWithClaims, SelectableColumn } from '@paintscout/ui-business'
import ArchiveIcon from '@material-ui/icons/Archive'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import DemoIcon from '@material-ui/icons/ErrorOutline'
import CopyIcon from '@material-ui/icons/FileCopy'
import UnarchiveIcon from '@material-ui/icons/Unarchive'
import type { LabelsOption } from '@paintscout/util/builder'
import AddIcon from '@material-ui/icons/Add'
import {
  formatCurrency,
  getFeature,
  getObjectLabels,
  getQuoteTypeOptions,
  getTags,
  isUserOwner,
  isUserAssigned
} from '@paintscout/util/builder'
import * as Sentry from '@sentry/react'
import get from 'lodash/get'
import keyBy from 'lodash/keyBy'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import numeral from 'numeral'
import type { OptionsDocument, Permission, QuoteResult, QuoteTag } from 'paintscout'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuoteMutations } from 'shared/hooks'
import { useFlags } from 'shared/components/common/FlagsProvider'
import CopyQuoteDialog from 'src/components/dialogs/CopyQuoteDialog'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import classnames from 'classnames'

import addressFormat from 'address-format'
import { track } from 'shared/util/analytics'
import StatusColumn from './StatusColumn'
import MobileViewColumn from './MobileViewColumn'
import { removeAuth0Prefix } from '@paintscout/util'

export interface QuoteTableProps {
  quotes: QuoteResult[]
  classes?: StyleClasses<typeof useStyles>
  loading?: boolean
  totalRows: number
  rowsPerPage: number
  isInvoice?: boolean
  checkboxes?: boolean
  page: number
  pages: number
  allowCustomColumns?: boolean

  sort?: string

  /**
   * Forces the table to render its dense (mobile) view
   */
  dense?: boolean

  /**
   * Hide estimator name
   */
  hideName?: boolean

  /**
   * Show status name(despite being dense)
   */
  showStatusName?: boolean

  /**
   * Hide/show default headers
   */
  hideHeaders?: {
    [name: string]: boolean
  }

  /**
   * Use pre tax in value for dashboard v2
   */
  preTaxValue?: boolean

  /**
   * Hides the pagination controls
   */
  disablePagination?: boolean

  bulkActions?: {
    archive?: boolean
    unarchive?: boolean
    delete?: boolean
  }

  /**
   * Allows bulk actions to be used when the table is in a dialog
   */
  actionBarAboveBackdrop?: boolean
  actionsForRow?: (row: QuoteRow) => {
    edit?: boolean
    archive?: boolean
    unarchive?: boolean
    invoice?: boolean
    copy?: boolean
    delete?: boolean
    remove?: boolean
  }
  noResultsMessage?: React.ReactNode
  onFetchNextPage?: (page: number) => any
  onPageChange?: (page: number) => any

  onDelete?: (ids: string[]) => any
  onRemove?: (id: string) => any
  onArchive?: (ids: string[], unarchive?: boolean) => any

  onRequestSort?: (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => void

  isContactPage?: boolean
  title?: string
  handleCreate?: () => void

  // Override renderRow function
  renderRow?: (row: QuoteRow) => JSX.Element
}

export interface QuoteRow {
  _id: string
  _rev: string
  invoice: boolean
  number: number
  client: string
  estimator: string
  archived: boolean
  isAssigned: boolean
  isOwner: boolean
  status: string
  phoneNumber: string
  location: React.ReactNode
  quote_date: number
  acceptedDate: number
  value: number
  error: boolean
  quoteType: string
  tags: QuoteTag[]
  disableRedirect?: boolean
  jobIdentifier: string
  addedOptionsTotal: number
  additionalWorkTotal: number
  amountPaid: number
  balanceDue: number
  hours: number
  hideName?: boolean
  showStatusName?: boolean
}

export const useStyles = makeStyles<Theme, { isMobile: boolean; allowHorizontalScroll: boolean; classes: any }>(
  (theme) => ({
    root: {
      overflow: 'scroll',
      padding: `0 ${theme.spacing()}px`,
      marginBottom: -1 // hide the divider of the last item if pagination is disabled
    },
    titleContainer: {
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      height: '48px'
    },
    actionButton: {
      paddingRight: '14px'
    },
    card: ({ allowHorizontalScroll }) => ({
      border: 'none',
      ...(allowHorizontalScroll && {
        overflowX: 'auto',
        transform: 'scaleY(-1)',
        '& > *': {
          transform: 'scaleY(-1)'
        },
        // flip the box shadow
        boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)'
      })
    }),
    tableHead: {
      '& div': {
        padding: theme.spacing(0, 1),
        height: '57px'
      }
    },
    tableCellHeader: {
      position: 'relative',
      whiteSpace: 'nowrap',
      // display: 'flex',
      // flexDirection: 'row',
      // justifyContent: 'flex-end',
      '& span': {
        lineHeight: theme.typography.pxToRem(24)
      },
      '& svg': {
        // position: 'absolute',
        // top: 3,
        // transform: 'translateY(50%)',
        height: 16,
        verticalAlign: 'middle'
      }
    },
    sortableTableCellHeader: {
      cursor: 'pointer'
    },
    // wish we could do a ({ isMobile }) => ({ '& $tableHead': {} }), but there
    // are issues using jss class selectors with dynamic classes https://github.com/mui-org/material-ui/issues/15511
    tableHeadMobile: {
      display: 'none'
    },
    quoteValue: {
      fontWeight: theme.typography.fontWeightMedium
    },
    locationColumn: {
      wordBreak: 'break-all',
      minWidth: 200
    },
    dateColumn: {
      whiteSpace: 'nowrap'
    },
    sortIcon: {
      color: '#fff',
      '&:hover': {
        color: '#fff'
      }
    },
    statusColumnCell: {
      width: '1%',
      paddingRight: 0,
      verticalAlign: 'top',
      paddingTop: theme.spacing(2.25)
    },
    mobileViewColumnCell: {
      paddingBottom: theme.spacing(0.75)
    },
    archiveIcon: {
      color: theme.paintscout.palette.brand.Neutral.P60
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    }
  }),
  { name: 'QuoteTable' }
)

/**
 * Displays quotes in a ResultsTable
 */
function QuoteTable({
  actionsForRow = () => ({}),
  allowCustomColumns,
  disablePagination,
  bulkActions = {},
  onFetchNextPage,
  showStatusName,
  onPageChange,
  rowsPerPage,
  checkboxes,
  isInvoice,
  onArchive,
  onDelete,
  onRequestSort,
  actionBarAboveBackdrop,
  hideHeaders = {},
  preTaxValue,
  isContactPage,
  onRemove,
  hideName,
  loading,
  quotes,
  dense,
  sort,
  title,
  handleCreate,
  noResultsMessage,
  classes: propClasses,
  ...otherProps
}: QuoteTableProps) {
  const theme = useTheme<Theme>()
  const { options } = useClientOptions()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) || dense
  const classes = useStyles({
    isMobile,
    allowHorizontalScroll: allowCustomColumns || isContactPage,
    classes: propClasses
  })
  const { canDeactivateSettingsItems } = useFlags()
  const multipleInvoicesFeature = getFeature({ options, path: 'quotes.multipleInvoices' })
  const quoteTypeOptions = keyBy(
    getQuoteTypeOptions({ options, deleted: true, canDeactivate: !!canDeactivateSettingsItems }),
    'key'
  )
  const objectLabels = getObjectLabels({ options, invoice: isInvoice })
  const { deleteQuotes, archiveQuotes } = useQuoteMutations()
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const { user, trialExpired, hasPermissions } = useUser()
  const history = useHistory()
  const { permissionToCreateInvoice, permissionToCreateQuote } = useUserPermissions()

  const localStorageColumns = JSON.parse(localStorage.getItem('quoteTableColumns'))
  const defaultColumns: SelectableColumn[] = [
    { name: '#', field: 'number', sortable: true, selected: !hideHeaders['number'] || true },
    { name: 'Status', field: 'status', selected: !hideHeaders['status'] || true },
    { name: 'Archived', field: 'archived' },
    {
      name: 'Date Created',
      field: 'quote_date',
      sortable: true,
      selected: !hideHeaders['quote_date'] || true,
      minWidth: 150
    },
    { name: 'Date Accepted', field: 'acceptedDate', sortable: true, minWidth: 170 },
    { name: 'Type', field: 'quoteType', sortable: true, selected: !hideHeaders['quoteType'] || true },
    { name: 'Contact', field: 'client', sortable: true, selected: !hideHeaders['client'] || true },
    { name: 'Phone Number', field: 'phoneNumber', align: 'center', minWidth: 150 },
    { name: 'Location', field: 'location', selected: !hideHeaders['location'] || true, minWidth: 200 },
    { name: 'Job Identifier', field: 'jobIdentifier', minWidth: 200 },
    { name: 'Estimator', field: 'estimator', sortable: true, selected: !hideHeaders['estimator'] || true },
    { name: 'Hours', field: 'hours', sortable: true, align: 'right' },
    { name: 'Added Options', field: 'addedOptionsTotal', sortable: true, align: 'right', minWidth: 200 },
    { name: 'Additional Work', field: 'additionalWorkTotal', sortable: true, align: 'right', minWidth: 200 },
    { name: 'Balance Due', field: 'balanceDue', sortable: true, align: 'right', minWidth: 150 },
    { name: 'Amount Paid', field: 'amountPaid', sortable: true, align: 'right', minWidth: 150 },
    { name: 'Value', field: 'value', sortable: true, align: 'right', selected: !hideHeaders['value'] || true }
  ].map((column) => ({
    ...(column as SelectableColumn),
    selected: localStorageColumns && allowCustomColumns ? localStorageColumns.includes(column.field) : column.selected
  }))

  const rows = quotes.map((quote: QuoteResult) =>
    mapQuoteToRow({ quote, quoteTypes: quoteTypeOptions, user, options, preTaxValue })
  )
  const [columns, setColumns] = useState<SelectableColumn[]>(defaultColumns)
  const [selectedRows, setSelectedRows] = useState<QuoteRow[]>([])
  const { allUnarchiveable, allArchiveable } = useMemo(() => {
    return selectedRows.reduce(
      (prev, current) => {
        if (current.archived) {
          return {
            ...prev,
            allArchiveable: false
          }
        } else {
          return {
            ...prev,
            allUnarchiveable: false
          }
        }
      },
      {
        allUnarchiveable: true,
        allArchiveable: true
      }
    )
  }, [selectedRows])

  // update selectedRows when quotes change
  useEffect(() => {
    setSelectedRows([])
  }, [quotes])

  const handleColumnsChange = (selected: string[]) => {
    localStorage.setItem('quoteTableColumns', JSON.stringify(selected))
    setColumns(
      defaultColumns.map((column) => ({
        ...column,
        selected: selected.includes(column.field)
      }))
    )
  }

  const handlePageChange = (page: number) => {
    const nextPageFirstIndex = (page - 1) * rowsPerPage
    const dataExists = !!quotes[nextPageFirstIndex]

    if (onPageChange) {
      onPageChange(page)
    }

    if (!dataExists && onFetchNextPage) {
      onFetchNextPage(page)
    }
  }

  const createSortHandler = (property: string) => (event: React.MouseEvent) => {
    if (onRequestSort) {
      // get current sort direction
      const currentSort = getSortDirection(property) || 'asc'

      // flip the sort direction
      const newSort = currentSort === 'asc' ? 'desc' : 'asc'

      onRequestSort(event, property, newSort)
    }
  }

  const getSortDirection = (property: string) => {
    const currentSortDirection = sort && sort.startsWith('-') ? 'desc' : 'asc'
    const currentSortName = sort && sort.startsWith('-') ? sort.slice(1) : sort

    if (currentSortName === property) {
      return currentSortDirection
    }
  }

  const handleAction = useCallback(
    (action: string, quoteRows: QuoteRow[]) => {
      const ids = quoteRows.map((quote) => quote._id)

      if (action === 'edit') {
        return history.push(`/${quoteRows[0].invoice ? 'invoices' : 'quotes'}/edit/${quoteRows[0]._id}`)
      }

      switch (action) {
        case 'remove':
          if (onRemove && ids[0]) {
            onRemove(ids[0])
          }
          break
        case 'copy':
          if (ids.length === 1) {
            openDialog(CopyQuoteDialog, {
              quoteId: ids[0],
              isInvoice: history?.location?.pathname?.includes('invoices')
            })
          }
          break
        case 'delete':
          openDialog(ConfirmationDialog, {
            message:
              ids.length > 1
                ? `These ${objectLabels.quote.plural.toLowerCase()} will no longer be accessible and you cannot undo this action.`
                : `This ${objectLabels.quote.value.toLowerCase()} will no longer be accessible and you cannot undo this action.`,
            closeButton: true,
            onConfirm: async () => {
              track('quotes_bulk_deleted', { quoteIds: ids, count: ids.length })
              await deleteQuotes(ids, true).catch((error) => {
                Sentry.captureException(error)
                enqueueSnackbar('Unable to delete', { variant: 'error' })
              })

              if (onDelete) {
                onDelete(ids)
              }

              setSelectedRows([])
              dismissDialog()
            },
            onCancel: dismissDialog
          })

          break

        case 'archive':
        case 'unarchive': {
          const unarchive = action === 'unarchive'

          openDialog(ConfirmationDialog, {
            message: (
              <div>
                <p>
                  This will {action} the selected{' '}
                  {ids.length > 0 ? objectLabels.quote.plural : objectLabels.quote.value}.{' '}
                  {!unarchive && (
                    <>
                      Archived {objectLabels.quote.plural} <strong>are not available offline!</strong>
                    </>
                  )}
                </p>
              </div>
            ),
            onConfirm: async () => {
              track(unarchive ? 'quotes_bulk_unarchived' : 'quotes_bulk_archived', { quoteIds: ids, count: ids.length })
              archiveQuotes(ids, { unarchive }).catch((error) => {
                Sentry.captureException(error)
                enqueueSnackbar(`Unable to ${unarchive ? 'unarchive' : 'archive'}`, { variant: 'error' })
              })

              if (onArchive) {
                onArchive(ids, unarchive)
              }

              setSelectedRows([])
              dismissDialog()
            },
            onCancel: dismissDialog
          })

          break
        }
      }
    },
    [onArchive, onDelete]
  )

  const rowActions = useCallback(
    (row: QuoteRow) => {
      const { isOwner, isAssigned } = row
      const canCreateType = row.invoice ? permissionToCreateInvoice : permissionToCreateQuote

      // isOwner and isAssigned are purposefully both checked against permAssigned to catch instances when a document owner is downgraded and loses permissions to that document type
      const permissionTo = (permAssigned: Permission, permAll: Permission[]) =>
        ((isOwner || isAssigned) && hasPermissions([permAssigned])) || hasPermissions(permAll, true)

      const canWrite = row.invoice
        ? permissionTo('write:assigned-invoices', ['write:all-invoices', 'write:all-documents'])
        : permissionTo('write:assigned-quotes', ['write:all-quotes', 'write:all-documents'])

      const allowedActions = []
      const actions = actionsForRow(row)

      if (actions.edit && canWrite) {
        allowedActions.push({ key: 'edit', label: 'Edit', icon: EditIcon })
      }
      if (actions.copy && canCreateType) {
        allowedActions.push({ key: 'copy', label: 'Duplicate', icon: CopyIcon })
      }
      if (actions.archive && canWrite) {
        allowedActions.push({ key: 'archive', label: 'Archive', icon: ArchiveIcon })
      }
      if (actions.unarchive && canWrite) {
        allowedActions.push({ key: 'unarchive', label: 'Unarchive', icon: UnarchiveIcon })
      }
      if (actions.delete && canWrite) {
        allowedActions.push({ key: 'delete', label: 'Delete', icon: DeleteIcon })
      }
      if (actions.remove && canWrite) {
        allowedActions.push({ key: 'remove', label: 'Remove Association', icon: LinkOffIcon })
      }

      return allowedActions
    },
    [actionsForRow, hasPermissions, permissionToCreateInvoice, permissionToCreateQuote]
  )

  const renderRow = useCallback(
    (row: QuoteRow) => {
      // mobile columns
      if (isMobile) {
        return (
          <TableRow key={row._id} data-testid={`quote-${row._id}`} classes={{ root: classes.tableRow }}>
            <QuoteTableCell row={row} align={'left'} className={classes.statusColumnCell}>
              <StatusColumn
                status={row.status}
                error={row.error}
                dense={dense && !showStatusName}
                archived={row.archived}
              />
            </QuoteTableCell>
            <QuoteTableCell row={row} className={classes.mobileViewColumnCell}>
              <MobileViewColumn row={row} hideName={hideName} />
            </QuoteTableCell>
            {(!title || isInvoice) && rowActions(row).length > 0 && (
              <TableCell align="right">
                <ActionButton
                  data-testid="actions"
                  actions={rowActions(row)}
                  disabled={rowActions(row).length === 0 || trialExpired}
                  onActionClick={(ev, action) => handleAction(action, [row])}
                />
              </TableCell>
            )}
          </TableRow>
        )
      }

      const dateFormat = options.options.dateFormat.momentValue
      // normal columns
      return (
        <TableRow key={row._id} data-testid={`quote-${row._id}`} classes={{ root: classes.tableRow }}>
          {checkboxes && (
            <TableCell padding="none">
              <Checkbox
                checked={!!selectedRows.find((r) => r._id === row._id)}
                onChange={(ev, checked) => {
                  setSelectedRows(checked ? [...selectedRows, row] : selectedRows.filter((r) => r._id !== row._id))
                }}
              />
            </TableCell>
          )}
          {columns.map((column) => {
            if (!column.selected) {
              return null
            }
            return (
              <QuoteTableCell key={column.field} align={column.align} row={row}>
                {getQuoteTableColumn({ key: column.field, row, dateFormat, classes, objectLabels, options })}
              </QuoteTableCell>
            )
          })}

          {rowActions.length > 0 && (
            <TableCell align="right">
              <ActionButton
                data-testid="actions"
                actions={rowActions(row)}
                disabled={rowActions(row).length === 0 || trialExpired}
                onActionClick={(ev, action) => handleAction(action, [row])}
              />
            </TableCell>
          )}
        </TableRow>
      )
    },
    [isMobile, rowActions, selectedRows, checkboxes, columns]
  )

  return (
    <>
      <Card className={classnames(classes.card, classes.quoteTableCard)} data-testid={'quote-list'}>
        {title && (
          <div className={classes.titleContainer}>
            <Typography variant="h3">{title}</Typography>
            {rows[0] && rowActions(rows[0]).length > 0 && !isInvoice && (
              <ActionButton
                data-testid="actions"
                actions={rowActions(rows[0])}
                onActionClick={(ev, action) => handleAction(action, [rows[0]])}
                className={classes.actionButton}
              />
            )}
            {(multipleInvoicesFeature || !rows[0]) && handleCreate && (
              <Button
                variant="hoverUnderline"
                color="primary"
                icon={AddIcon}
                onClick={handleCreate}
                className="tour-quote-table-new-button"
              >
                New
              </Button>
            )}
          </div>
        )}
        <ResultsTable
          classes={{
            table: classes.table,
            tableHead: isMobile ? classes.tableHeadMobile : classes.tableHead
          }}
          noResultsMessage={
            noResultsMessage
              ? noResultsMessage
              : isInvoice
                ? 'No invoices found.'
                : `No ${objectLabels.quote.plural.toLowerCase()} found.`
          }
          shimHeight={isMobile ? '55px' : '30px'}
          columns={
            <TableRow>
              {!isMobile && (
                <>
                  {checkboxes && <TableCell isControl padding="none" />}
                  {columns.map((column) => {
                    if (!column.selected) {
                      return null
                    }
                    const sortable = !!sort && !!column.sortable
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        fadeWhenInactive
                        sortable={sortable}
                        sortDirection={getSortDirection(column.field)}
                        onClick={sortable ? createSortHandler(column.field) : null}
                        style={{ minWidth: column.minWidth || undefined }}
                      >
                        {column.name}
                      </TableCell>
                    )
                  })}
                  {rowActions.length > 0 && (
                    <TableCell align="right">
                      {allowCustomColumns && (
                        <ResultsTableColumnSelect
                          columns={columns}
                          onConfirm={handleColumnsChange}
                          disabled={loading}
                        />
                      )}
                    </TableCell>
                  )}
                </>
              )}
              {isMobile && <TableCell>{objectLabels.quote.plural}</TableCell>}
            </TableRow>
          }
          renderRow={renderRow}
          rows={rows}
          rowsPerPage={rowsPerPage}
          disablePagination={disablePagination}
          loading={loading}
          {...otherProps}
          // otherProps wrappers
          onPageChange={handlePageChange}
        />
      </Card>
      <BulkActionBar
        itemName={objectLabels.quote.value}
        pluralItemName={objectLabels.quote.plural}
        selectedItems={selectedRows}
        onDeselectAll={() => setSelectedRows([])}
        aboveBackdrop={actionBarAboveBackdrop}
      >
        {bulkActions.archive && allArchiveable && (
          <BulkActionButton icon={ArchiveIcon} onClick={() => handleAction('archive', selectedRows)}>
            Archive
          </BulkActionButton>
        )}
        {bulkActions.unarchive && allUnarchiveable && (
          <BulkActionButton icon={UnarchiveIcon} onClick={() => handleAction('unarchive', selectedRows)}>
            Unarchive
          </BulkActionButton>
        )}
        {bulkActions.delete && (
          <BulkActionButton icon={DeleteIcon} onClick={() => handleAction('delete', selectedRows)}>
            Delete
          </BulkActionButton>
        )}
      </BulkActionBar>
    </>
  )
}

export function mapQuoteToRow({
  quote,
  quoteTypes,
  user,
  options,
  disableRedirect,
  preTaxValue
}: {
  quote: QuoteResult
  quoteTypes: any
  user: Auth0UserWithClaims
  options: OptionsDocument
  disableRedirect?: boolean
  preTaxValue?: boolean
}): QuoteRow {
  const { _id, _rev } = quote

  // QuoteResult (ES) has assignedUsers as string[], QuoteDocument (CouchDB) has it as an object map
  const isAssigned = Array.isArray(quote.assignedUsers)
    ? quote.assignedUsers.includes(removeAuth0Prefix(user.user_id))
    : isUserAssigned(quote as any, user.user_id)
  const isOwner = isUserOwner(quote, user.user_id)

  const quoteNumber = get(quote, 'number', 0)
  const client = [quote.contact?.first_name, quote.contact?.last_name].filter(Boolean).join(' ')
  const estimator = [quote.owner?.firstName, quote.owner?.lastName].filter(Boolean).join(' ')

  const status = quote.status.value
  const quote_date = quote.quote_date
  const acceptedDate = quote.acceptedDate

  const value = quote.totals ? (preTaxValue ? quote.totals.after_discount : quote.totals.after_tax) : null
  const error = quote.sendError ? quote.sendError : false

  const location = getLocation({ quote })

  let intExt = ''
  if (typeof quote?.int_ext === 'string') {
    intExt = quote.int_ext
  } else if (quote?.int_ext?.value) {
    intExt = quote.int_ext.value
  }

  const quoteType = quoteTypes[intExt] && quoteTypes[intExt].label ? quoteTypes[intExt].label : ''

  const archived = !!quote.archived
  const tags = quote?.tags ? getTags({ options, keys: quote.tags }) : []

  return {
    _id,
    _rev,
    invoice: !!quote.is_invoice,
    number: quoteNumber,
    quoteType,
    archived,
    client,
    estimator,
    isAssigned,
    isOwner,
    status,
    location,
    quote_date,
    acceptedDate,
    value,
    error,
    tags,
    phoneNumber: quote?.contact?.phone_number ?? '',
    disableRedirect,
    jobIdentifier: quote.job_identifier,
    addedOptionsTotal: quote.addedOptionsTotal || 0,
    additionalWorkTotal: quote.additionalWorkTotal || 0,
    amountPaid: quote.amountPaid || 0,
    balanceDue: quote.balanceDue || 0,
    hours: Number(quote.totalHours?.toFixed(2)) || 0
  }
}

function getLocation({ quote }: { quote: QuoteResult }) {
  let location = ''

  const jobIdentifier = quote.job_identifier
  const jobAddress = addressFormat({
    address: quote.jobAddress && quote.jobAddress.address,
    city: quote.jobAddress && quote.jobAddress.city,
    postalCode: quote.jobAddress && quote.jobAddress.postal,
    subdivision: quote.jobAddress && quote.jobAddress.province,
    countryCode: 'US'
  })

  if (jobAddress && jobAddress.length > 0) {
    location += jobAddress.join('\n') + '\n'
  }

  if (jobIdentifier) {
    location = `${jobIdentifier}\n${location}`
  }

  if (!location) {
    // no job address - so fallback to contact address
    location = addressFormat({
      address: quote.contact?.address,
      city: quote.contact?.city,
      postalCode: quote.contact?.postal,
      subdivision: quote.contact?.province,
      countryCode: 'US'
    }).join('\n')
  }

  if (quote.demoTitle) {
    return <Chip color="primary" size="small" icon={<DemoIcon />} label={quote.demoTitle} />
  }

  return <>{location}</>
}

function QuoteTableCell({ row, ...other }: { row: QuoteRow } & TableCellProps) {
  return row.disableRedirect ? (
    <TableCell {...other} />
  ) : (
    /**
     * Renders a TableCell with a <Link> component to the quote
     */
    <TableCell
      component={(cProps: any) => (
        <Link to={{ pathname: `/${row.invoice ? 'invoices' : 'quotes'}/edit/${row._id}` }} {...cProps} />
      )}
      {...other}
    />
  )
}

interface QuoteTableColumnArgs {
  key: string
  row: QuoteRow
  dateFormat: string
  classes: ClassNameMap<string>
  objectLabels: LabelsOption
  options: OptionsDocument
}

function getQuoteTableColumn({ key, row, dateFormat, classes, objectLabels, options }: QuoteTableColumnArgs) {
  switch (key) {
    case 'number':
      return row.number && row.number !== -1 ? row.number : ''
    case 'status':
      return <StatusColumn status={row.status} error={row.error} archived={row.archived} tags={row.tags} />
    case 'quote_date':
      return <div className={classes.dateColumn}>{moment(row.quote_date).format(dateFormat)}</div>
    case 'acceptedDate':
      return <div className={classes.dateColumn}>{row.acceptedDate && moment(row.acceptedDate).format(dateFormat)}</div>
    case 'location':
    case 'jobIdentifier':
      return <div className={classes.locationColumn}>{row[key] || ''}</div>

    case 'value':
      return (
        <div className={classes.quoteValue}>
          {objectLabels.currency.symbol}
          {numeral(row.value).format('0,0.00')}
        </div>
      )
    case 'quoteType':
    case 'client':
    case 'phoneNumber':
    case 'estimator':
      return row[key] || ''

    case 'archived':
      return (
        <QuoteTableCell row={row}>{row.archived && <ArchiveIcon className={classes.archiveIcon} />}</QuoteTableCell>
      )
    case 'hours':
      return row.hours

    case 'balanceDue':
    case 'amountPaid':
    case 'addedOptionsTotal':
    case 'additionalWorkTotal':
      return formatCurrency({ options, value: row[key] })

    default:
      return row[key].toString()
  }
}

export default QuoteTable
